/**
 * منطقِ خالصِ صندوقِ بازخورد: نوعِ بازخورد، و خواندنِ لاگِ تشخیصیِ چسبیده به آن.
 *
 * ═══ چرا این‌جا و نه داخلِ خودِ سربرگِ ادمین ═══
 *
 * همان دلیلِ `sourceTest.ts`: تصمیم‌هایی که این‌جاست — «کدام سطرِ لاگ خطاست»
 * و «چند میلی‌ثانیه یعنی کُند» — با `npm test` آزموده می‌شوند، بدونِ مرورگر
 * و بدونِ دیتابیس. عددی که تعیین می‌کند آدم موقعِ خواندنِ یک گزارش چه چیزی
 * را **اول** می‌بیند، باید آزمودنی باشد.
 *
 * لاگِ تشخیصی را `core/lib/diagLine.ts` می‌سازد و شکلِ هر سطرش این است:
 *
 *     12.3s qry events.select 210ms rows=20
 *     13.1s ERR feed.load ✘ message=network request failed
 *
 * پس این فایل قرینه‌ی خواندنِ همان است: آن یکی می‌نویسد، این یکی از میانِ
 * سیصد سطر آن چند تایی را که مهم‌اند بیرون می‌کشد.
 */

/** سه دکمه‌ی فرمِ بازخورد — همان مقادیری که در `where_at` می‌نشینند. */
export type FeedbackKind = 'bug' | 'idea' | 'other';

const KINDS: FeedbackKind[] = ['bug', 'idea', 'other'];

/**
 * `feedback.bug` → `bug`.
 *
 * هر چیزِ ناشناخته `other` می‌شود و نه `null`: این تابع فقط برای برچسب زدن
 * است و ردیفی که نوعش را نمی‌شناسیم باز هم باید دیده شود. گم شدنِ یک
 * بازخورد بدتر از برچسبِ کمی نادقیق است.
 */
export function feedbackKind(whereAt: string | null | undefined): FeedbackKind {
  const k = (whereAt ?? '').split('.')[1] as FeedbackKind;
  return KINDS.includes(k) ? k : 'other';
}

/** برچسبِ فارسیِ هر نوع — مخاطبش پنلِ ادمین است، پس ترجمه نمی‌خواهد. */
export const FEEDBACK_LABEL: Record<FeedbackKind, string> = {
  bug: 'یک چیزی خراب است',
  idea: 'یک ایده دارم',
  other: 'چیز دیگری',
};

/** نشانه‌ی کوتاهِ کنارِ هر ردیف. */
export const FEEDBACK_ICON: Record<FeedbackKind, string> = {
  bug: '🐞',
  idea: '💡',
  other: '💬',
};

/**
 * کوئری‌ای که از این کندتر باشد، در گزارش برجسته می‌شود.
 *
 * یک ثانیه است و نه دویست میلی‌ثانیه: این عدد قرار نیست معیارِ سلامتِ اپ
 * باشد (آن کارِ `npm run log:pull` است که p50/p95 حساب می‌کند)، قرار است در
 * لاگِ یک نفر که گفته «کُند بود» آن چند سطری را نشان دهد که واقعاً حس
 * می‌شوند. زیرِ یک ثانیه کسی چیزی نمی‌گوید.
 */
export const DIAG_SLOW_MS = 1000;

/** چند سطرِ برجسته حداکثر برگردانده می‌شود — بقیه در خودِ متنِ کامل هستند. */
export const DIAG_MAX_HIGHLIGHTS = 12;

export interface DiagHighlights {
  /** سطرهایی که شکست خورده‌اند (`✘`) یا نوعشان خطاست. */
  errors: string[];
  /** سطرهایی که از `DIAG_SLOW_MS` کندترند، از کندترین به بالا. */
  slow: string[];
  /** چند سطرِ واقعی در متن هست (سرآیند و جداکننده شمرده نمی‌شوند). */
  rows: number;
}

/** `  12.3s qry events.select 210ms rows=20` → آیا اصلاً یک سطرِ لاگ است. */
const isRow = (line: string): boolean => /^\s*\d+(\.\d+)?s\s/.test(line);

/**
 * میلی‌ثانیه‌ی همان سطر.
 *
 * ⚠ **اولین** `<عدد>ms` گرفته می‌شود و نه هر کدام: مدتِ خودِ کار همیشه بلافاصله
 * بعدِ نامش می‌آید، ولی `detail` هم ممکن است چیزی مثلِ `timeout=5000ms` داشته
 * باشد. گرفتنِ آخری یعنی سطری که ۵ میلی‌ثانیه طول کشیده، «کُند» گزارش شود.
 */
export function diagLineMs(line: string): number | null {
  const m = /\s(\d+)ms(\s|$)/.exec(line);
  return m ? Number(m[1]) : null;
}

/** آیا این سطر شکست است. `✘` را `formatDiag` برای `ok === false` می‌گذارد. */
export const diagLineFailed = (line: string): boolean =>
  line.includes('✘') || /^\s*\d+(\.\d+)?s\s+ERR\s/.test(line);

/**
 * از متنِ کاملِ گزارش، آن چند سطری که آدم دنبالشان است.
 *
 * ورودی همان چیزی است که `sendDiag` فرستاده — سرآیند، سطرهای این نشست، و
 * (اگر بوده) نشستِ قبلی. هر سه با هم اسکن می‌شوند: نشستِ قبلی دقیقاً همان
 * جایی است که کرشِ گزارش‌شده افتاده.
 */
export function diagHighlights(text: string | null | undefined): DiagHighlights {
  const lines = (text ?? '').split('\n').filter(isRow);
  const errors: string[] = [];
  const slow: { line: string; ms: number }[] = [];

  for (const line of lines) {
    if (diagLineFailed(line)) {
      errors.push(line.trim());
      continue;
    }
    const ms = diagLineMs(line);
    if (ms != null && ms >= DIAG_SLOW_MS) slow.push({ line: line.trim(), ms });
  }

  slow.sort((a, b) => b.ms - a.ms);

  return {
    // **آخرین** خطاها و نه اولی‌ها: کسی که گزارش می‌دهد همین حالا به مشکل
    // خورده، پس چیزی که دنبالش هستیم تهِ لاگ است. با بریدن از سر، یک نشستِ
    // پرخطا دقیقاً همان سطری را می‌انداخت که به‌خاطرش گزارش داده شده.
    errors: errors.slice(-DIAG_MAX_HIGHLIGHTS),
    slow: slow.slice(0, DIAG_MAX_HIGHLIGHTS).map((s) => s.line),
    rows: lines.length,
  };
}
